//Node Modules
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useRecoilValue, useRecoilValueLoadable } from "recoil";

//Atoms
import { deviceSensorAlarmListAtom } from "../../atoms/alarm";
import { devicesStateSelector } from "../../atoms/dashboard";

//Helpers
import { useDeviceOfflineState } from "../../helpers/useDeviceOfflineState";

//Other
import { nav } from "../../config/navigation";

const LOW_BATTERY_THRESHOLD = 20;

const getClosedValveCount = (reportedState) => {
	const statuses = [
		reportedState?.valves?.valve1?.status,
		reportedState?.valves?.valve2?.status,
		reportedState?.relay?.status,
	];
	return statuses.filter((status) => status === true || status === 1).length;
};

const Tile = ({ to, value, label, severity }) => (
	<Link to={to} className={`card healthTile flexVert aItems-center gap-xsmall ${severity}`}>
		<span className="healthTile-value">{value ?? "-"}</span>
		<span className="fontSize-small">{label}</span>
	</Link>
);

const HealthTiles = () => {
	const { t } = useTranslation();
	const activeAlarms = useRecoilValue(deviceSensorAlarmListAtom);
	const { state, contents: devices } = useRecoilValueLoadable(devicesStateSelector);
	const getOfflineState = useDeviceOfflineState();

	const alarmCount = activeAlarms?.length ?? 0;

	let offlineCount = null;
	let lowBatteryCount = null;
	let closedValveCount = null;

	if (state === "hasValue") {
		offlineCount = devices.filter((device) => getOfflineState(device).isOffline).length;

		lowBatteryCount = devices.filter((device) => {
			const percentage = Number(device.reportedState?.battery?.percentage);
			return Number.isFinite(percentage) && percentage < LOW_BATTERY_THRESHOLD;
		}).length;

		closedValveCount = devices.reduce((acc, device) => acc + getClosedValveCount(device.reportedState), 0);
	}

	return (
		<div className="grid columns-4 gap-medium">
			<Tile
				to={`${nav.alarm.base}/${nav.alarm.alerts}`}
				value={alarmCount}
				label={t("dashboard.health.alarms")}
				severity={alarmCount > 0 ? "tileError" : "tileSuccess"}
			/>
			<Tile
				to={`${nav.device.base}/${nav.device.management}?health=offline`}
				value={offlineCount}
				label={t("dashboard.health.offline")}
				severity={offlineCount > 0 ? "tileWarn" : "tileSuccess"}
			/>
			<Tile
				to={`${nav.device.base}/${nav.device.management}?health=battery-low`}
				value={lowBatteryCount}
				label={t("dashboard.health.lowBattery")}
				severity={lowBatteryCount > 0 ? "tileWarn" : "tileSuccess"}
			/>
			<Tile
				to={`${nav.device.base}/${nav.device.management}?health=valves-closed`}
				value={closedValveCount}
				label={t("dashboard.health.valvesClosed")}
				severity={closedValveCount > 0 ? "tileWarn" : "tileSuccess"}
			/>
		</div>
	);
};

export default HealthTiles;
